/**
 * Yerel ayara duyarlı okuma — eski ayrıştırıcıya köprü.
 *
 * Eski `js/ai-files-parser.js` bütün tarih ve tutar okumalarını iki
 * fonksiyondan geçiriyor: `isoFromStr` ve `parseAmt`. Bu iki geçidi
 * `@tekoffice/extract`e bağlamak Excel, CSV, SpreadsheetML ve düz metin
 * yollarının HEPSİNİ birden düzeltir; her yolu ayrı ayrı elden geçirmeye
 * gerek kalmaz.
 *
 * Burada SESSİZ GERİ DÜŞÜŞ YOKTUR. Köprü yüklenmemişse eski (hatalı) koda
 * dönmek, düzeltmeye çalıştığımız sessiz yanlış veriyi geri getirirdi.
 * Bunun yerine okuma başarısız sayılır ve çağıran açık bir uyarı gösterir.
 *
 * KALDIRMAK İÇİN: bu klasörü sil, shell/index.ts listesinden satırı çıkar.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import {
  parseAmount, parseDate, detectDateOrder, isAmbiguous, duplicateFlags,
  parseEInvoice, isEInvoiceAttachment, toLedgerRows,
  checkVatArithmetic, reconcileTotal, findDeclaredTotal,
  localeFromBrowser, resolveLocale, FALLBACK,
  profileFor, isDocKind, applyDirection, againstTheGrain, findStatementBalances, checkStatementBalance,
  type Locale, type DateOrder, type Parsed, type DocKind,
} from '@tekoffice/extract';

/** Hesabın yerel ayarı. Giriş yapılınca profilden tazelenir. */
let current: Locale = FALLBACK;

/** Belgenin kendi kanıtladığı gün/ay sırası. Her belgede sıfırlanır. */
let evidence: DateOrder | null = null;

/**
 * Son okumanın belirsizliği.
 *
 * Eski ayrıştırıcı düz değer bekliyor (sayı / ISO metin); imzayı
 * değiştirmemek için belirsizlik yan kanaldan taşınır. Çağrı ile okuma
 * arasında başka iş yapılmadığı (ikisi de eşzamanlı ve yan yana) için
 * güvenli; yine de tek kullanımlıktır: okundu mu temizlenir.
 */
let pending: { reason: string; alternative?: unknown } | null = null;

/** Kullanıcının seçtiği belge türü. Oturum boyunca kalır: art arda fiş
 *  yükleyen biri her seferinde yeniden seçmek zorunda kalmasın. */
let kind: DocKind = 'other';

/** Seçiciyi seçili türe göre boya. Seçici yoksa (başka ekran) sessiz geçer. */
function paintKind(): void {
  document.querySelectorAll<HTMLElement>('[data-doc-kind]').forEach((b) => {
    const on = b.dataset.docKind === kind;
    b.classList.toggle('on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

/* Sorulup sorulmayacagina isAmbiguous karar verir; kural pakette ve
   testlidir (parsed.test.ts), burada tekrarlanmaz. */
function record<T>(r: Parsed<T>): void {
  pending = isAmbiguous(r)
    ? { reason: r.reason ?? '', alternative: r.alternative }
    : null;
}

/**
 * Hesabın yerel ayarını belirle.
 *
 * Tarayıcı dili temel alınır. Para birimi buradan TÜRETİLMEZ — profilde ne
 * yazıyorsa o geçerlidir; Almanya'da yaşayan biri USD ile defter tutuyor
 * olabilir.
 */
function refresh(): void {
  const user = legacy.user as { setup?: { locale?: string; currency?: string } } | null;
  const explicit = user?.setup?.locale;
  /* Arayüz dili artık okuma yerelini ETKİLEMEZ: arayüz yalnızca İngilizce,
     belge ise Türkçe ya da Almanca biçimde olabilir. */
  current = explicit ? resolveLocale(explicit) : localeFromBrowser();
}

export default defineFeature({
  id: 'extract',
  label: 'Yerel ayara duyarlı belge okuma',
  view: null,

  boot: () => { refresh(); paintKind(); },

  legacyGlobals: {
    /** Eski `parseAmt` bunun üstüne biner. Okunamazsa NaN — 0 DEĞİL. */
    exAmount: (raw: unknown) => {
      const r = parseAmount(raw, current);
      record(r);
      return r.value === null ? NaN : r.value;
    },

    /** Eski `isoFromStr` bunun üstüne biner. Okunamazsa null. */
    exDate: (raw: unknown) => {
      const r = parseDate(raw, current, { evidence });
      record(r);
      return r.value;
    },

    /** Son okuma belirsiz miydi? Okunduğunda temizlenir. */
    exTakeDoubt: () => { const p = pending; pending = null; return p; },

    /** Belgeyi taramadan önce çağrılır: belgenin kendi kanıtını arar. */
    exScanEvidence: (samples: unknown) => {
      evidence = Array.isArray(samples)
        ? detectDateOrder(samples.map((s) => String(s ?? '')))
        : null;
      return evidence;
    },

    /** Köprü yüklendi mi? Eski kod bunu kontrol edip açık uyarı gösterir. */
    exReady: () => true,

    /* ── Yapılandırılmış e-fatura ────────────────────────────────
       Belge makine okunur veri taşıyorsa ONU oku. Tarih belirsizliği yok,
       ondalık ayracı belirsizliği yok, kaçan kalem yok — tahmin sıfır. */

    /** XML metnini defter satırlarına çevir. E-fatura değilse null. */
    exReadEInvoice: (xml: unknown, type: unknown) => {
      const inv = parseEInvoice(String(xml ?? ''));
      if (!inv) return null;
      const dir = type === 'income' ? 'income' : 'expense';
      return {
        syntax: inv.syntax,
        invoiceId: inv.invoiceId,
        seller: inv.sellerName,
        buyer: inv.buyerName,
        netTotal: inv.netTotal,
        taxTotal: inv.taxTotal,
        grossTotal: inv.grossTotal,
        rows: toLedgerRows(inv, dir, current.currency),
      };
    },

    /** Bu PDF eki bir Factur-X/ZUGFeRD faturası mı? */
    exIsEInvoiceAttachment: (name: unknown) => isEInvoiceAttachment(String(name ?? '')),

    /* ── Aritmetik doğrulama ─────────────────────────────────────
       Hem bizim kaçırdığımız kalemi hem belgenin kendi hatasını yakalar. */

    /** net + vergi = brüt mü? Tutmuyorsa açıklama döner. */
    exCheckVat: (net: unknown, tax: unknown, gross: unknown) => {
      const n = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
      const r = checkVatArithmetic(n(net), n(tax), n(gross));
      return r.ok ? null : { reason: r.reason, difference: r.difference };
    },

    /** Metinde ilan edilmiş genel toplamı ara. Yoksa null. */
    exDeclaredTotal: (lines: unknown) =>
      Array.isArray(lines)
        ? findDeclaredTotal(
            lines.map((l) => String(l ?? '')),
            (raw) => parseAmount(raw, current).value,
          )
        : null,

    /** Okunan kalemler ilan edilen toplamla tutuyor mu? */
    exReconcile: (amounts: unknown, declared: unknown) => {
      if (!Array.isArray(amounts)) return null;
      const d = declared === null || declared === undefined ? null : Number(declared);
      const r = reconcileTotal(amounts.map((a) => Number(a) || 0), d);
      return r.ok ? null : { reason: r.reason, difference: r.difference };
    },

    /** Birebir ayni satirlari isaretle. Kural pakette ve testli
     *  (duplicate.test.ts); burada tekrarlanmaz. */
    exDuplicateFlags: (rows: unknown) =>
      Array.isArray(rows)
        ? duplicateFlags(rows.map((r) => ({
            date: String((r as { date?: unknown }).date ?? ''),
            amount: Number((r as { amount?: unknown }).amount ?? 0),
            type: String((r as { type?: unknown }).type ?? ''),
            note: String((r as { note?: unknown }).note ?? ''),
          })))
        : [],

    /**
     * Yeni hesap için varsayılan para birimi.
     *
     * `auth.js` bugüne kadar her yeni hesabı koşulsuz `TRY` yaratıyordu —
     * dünyanın neresinde açılırsa açılsın. Yanlış para birimi her tutarı
     * sessizce bozar ve kullanıcı fark etmez.
     *
     * Artık tarayıcının yerel ayarından türetilir. Tanınmayan bir yerel ayar
     * için USD seçildi: uluslararası en yaygın ortak zemin odur ve her hâlde
     * "herkes Türk Lirası kullanıyor" varsayımından daha az yanlıştır.
     * Kullanıcı kurulumda değiştirebilir; burası yalnızca ilk değerdir.
     */
    exDefaultCurrency: () => current.currency || 'USD',

    /** Geçerli yerel ayar — arayüzde göstermek ve hata ayıklamak için. */
    exLocale: () => ({ ...current }),

    /** Giriş/profil değişiminde eski kod bunu çağırır. */
    exRefresh: () => { refresh(); return current.tag; },

    /* ── Okuma profili: kullanıcı ne yüklediğini söyler ─────────────
       Kural pakette ve testli (profiles.test.ts); burada tekrarlanmaz. */

    /** Seçiciden çağrılır. Bilinmeyen değer yok sayılır. */
    exPickKind: (value: unknown) => {
      if (isDocKind(value)) kind = value;
      paintKind();
      return kind;
    },

    exKind: () => kind,

    /** 'declared' | 'balance' | 'none' — hangi toplam kontrolü çalışacak. */
    exKindCheck: () => profileFor(kind).totalCheck,

    /** AI'ya eklenecek dar görev. 'other' için boş dize. */
    /* AI ipucu metni sunucuda türden üretilir (Worker ai-tasks); tarayıcı yalnız türü yollar. */
    exCurrentKind: () => kind,

    /** Seçilen türün yönünü satırlara uygula. Eski kod diziyi yerinde
     *  kullandığı için sonuç aynı diziye geri yazılır. Ters işaretli satırlar
     *  (indirim, iade) sessizce çevrilmez; satıra soru olarak eklenir. */
    exApplyKind: (rows: unknown) => {
      if (!Array.isArray(rows)) return rows;
      type LegacyRow = { type?: unknown; amount?: unknown; doubt?: unknown };
      const src = rows as LegacyRow[];
      const flagged = againstTheGrain(src, kind);
      const tie = flagged.length > 0 && flagged.length === src.length;
      const out = applyDirection(src, kind);
      for (let i = 0; i < out.length; i++) rows[i] = out[i];
      for (const i of flagged) {
        const r = rows[i] as LegacyRow;
        const reason = tie
          ? 'Lines on this document have opposite signs and it is unclear which are discounts or refunds. Check the direction of this line.'
          : `This line has the opposite sign to the rest of the document (a discount, credit or refund?). It was recorded as ${r.type === 'income' ? 'income' : 'an expense'} so the total stays right - please check.`;
        const prev = Array.isArray(r.doubt) ? r.doubt : [];
        r.doubt = prev.concat([{ field: 'direction', reason }]);
      }
      return rows;
    },

    /** Ekstre: açılış + hareketler = kapanış mı? Tutmuyorsa açıklama. */
    exStatementCheck: (lines: unknown, rows: unknown) => {
      if (!Array.isArray(lines) || !Array.isArray(rows)) return null;
      const b = findStatementBalances(
        lines.map((l) => String(l ?? '')),
        (raw) => parseAmount(raw, current).value,
      );
      const r = checkStatementBalance(b.opening, b.closing, rows as { amount: unknown; type: unknown }[]);
      return r.ok ? null : { reason: r.reason, difference: r.difference };
    },
  },
});
